import { get } from "lodash";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import { useIsMobile } from "routes/group/hooks/useIsMobile";
import { isHaveActionPermission } from "utills/helpers";
import { EventType, GroupType, PagingType } from "routes/group/types";
import { ORDER_STATUS_OPTIONS } from "../FilterBar";
import Button from "components/button";
import Pagination from "components/pagination";
import { Tag } from "components/tag";

interface PropType {
  events: EventType[];
  data: GroupType[];
  paging: PagingType | null;
  handleChangePage: (page: number) => void;
}

const DataTable = (props: PropType) => {
  const { events, data, paging, handleChangePage } = props;

  const PAGE_SIZE = 10;

  const isMobile = useIsMobile(1024);
  const navigate = useNavigate();

  const handleGetEventInfomation = (event_id: string, events: EventType[]) => {
    if (!events) return null;

    return events?.find((event) => event?.id === event_id) ?? null;
  };

  const handleFormatStatus = (value: string) => {
    return (
      ORDER_STATUS_OPTIONS.find((status) => status.value === value) ?? null
    );
  };

  const handleformatDate = (value: string) => {
    const m = moment(value, "YYYY-MM-DD HH:mm:ss", true); // strict
    return m.isValid() ? m.format("DD-MM-YYYY HH:mm:ss") : "";
  };

  return (
    <section className="box-container flex flex-col gap-5">
      {isMobile ? (
        <div className="mb-6 w-full flex flex-col gap-[8px]">
          {data?.map((item: GroupType, index: number) => {
            const createdAt = handleformatDate(get(item, "created_at", ""));
            const expiredAt = handleformatDate(get(item, "expired_date", ""));

            return (
              <div
                key={index}
                className="w-full bg-white p-[16px] rounded-[8px] shadow-md"
              >
                <div className="w-full flex items-center justify-between">
                  <div className="opacity-0">action</div>

                  <div className="flex items-center gap-2">
                    {isHaveActionPermission(
                      "",
                      "group_buy_management",
                      "view"
                    ) && (
                      <Button
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                        onClick={() => {
                          navigate(
                            `/group-management/detail/${get(item, "id", "")}`
                          );
                        }}
                      >
                        <EyeIcon className="w-4 h-4 text-black" />
                      </Button>
                    )}

                    {isHaveActionPermission(
                      "",
                      "group_buy_management",
                      "edit"
                    ) && (
                      <Button
                        className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                        onClick={() => {
                          navigate(
                            `/group-management/edit/${get(
                              item,
                              "id",
                              ""
                            )}?event_id=${get(item, "event_id", "")}`
                          );
                        }}
                      >
                        <PencilIcon className="w-4 h-4 text-black" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="w-full mt-[16px] flex flex-col gap-[8px]">
                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      No
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                        1 +
                        index}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Group ID
                    </span>
                    <Link
                      to={`/group-management/detail/${get(item, "id", 0)}`}
                      className="w-[70%] font-medium text-brand-500 hover:underline break-words text-right"
                    >
                      {get(item, "id", 0)}
                    </Link>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Group Name
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "group_name_en", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Event
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {handleGetEventInfomation(
                        get(item, "event_id", ""),
                        events
                      )?.name_en ?? ""}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Member Quantity
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {get(item, "num_of_mem", "")}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Status
                    </span>
                    <span className="w-[70%] text-right">
                      <Tag
                        text={
                          handleFormatStatus(get(item, "status", "").toString())
                            ?.label ?? ""
                        }
                        type={
                          handleFormatStatus(get(item, "status", "").toString())
                            ?.color ?? ("" as any)
                        }
                      />
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Created At
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {createdAt}
                    </span>
                  </div>

                  <div className="w-full flex">
                    <span className="w-[30%] text-[14px] font-[500] text-[#262626]">
                      Expired At
                    </span>
                    <span className="w-[70%] text-[14px] font-[400] text-[#2B3033] text-right">
                      {expiredAt}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flow-root">
          <div className="relative overflow-x-auto min-w-full">
            <table className="mb-6 w-full divide-y divide-gray-300 bg-magnolia-500 rounded-md">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="th py-3.5 pl-4 pr-3 sm:pl-6 lg:pl-8"
                  >
                    No
                  </th>

                  <th scope="col" className="th">
                    Group ID
                  </th>

                  <th scope="col" className="th">
                    Group Name
                  </th>

                  <th scope="col" className="th">
                    Event
                  </th>

                  <th scope="col" className="th">
                    Member Quantity
                  </th>

                  <th scope="col" className="th">
                    Status
                  </th>

                  <th scope="col" className="th">
                    Created At
                  </th>

                  <th scope="col" className="th">
                    Expired At
                  </th>

                  <th scope="col" className="th w-28">
                    <span className="sr-only">Action</span>
                    <p className="">Action</p>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 bg-white">
                {data?.map((item: GroupType, index: number) => {
                  const createdAt = handleformatDate(
                    get(item, "created_at", "")
                  );

                  const expiredAt = handleformatDate(
                    get(item, "expired_date", "")
                  );

                  return (
                    <tr key={index}>
                      <td className="td py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                        {(get(paging, "current_page", 1) - 1) * PAGE_SIZE +
                          1 +
                          index}
                      </td>

                      <td className="td min-w-[300px] w-fit font-medium text-brand-500 break-words">
                        <Link
                          to={`/group-management/detail/${get(item, "id", 0)}`}
                          className="w-fit font-medium text-brand-500 hover:underline"
                        >
                          {get(item, "id", 0)}
                        </Link>
                      </td>

                      <td className="td min-w-[230px]">
                        {get(item, "group_name_en", "")}
                      </td>

                      <td className="td min-w-[230px]">
                        {handleGetEventInfomation(
                          get(item, "event_id", ""),
                          events
                        )?.name_en ?? ""}
                      </td>

                      <td className="td min-w-[160px]">
                        {get(item, "num_of_mem", "")}
                      </td>

                      <td className="td min-w-40">
                        <Tag
                          text={
                            handleFormatStatus(
                              get(item, "status", "").toString()
                            )?.label ?? ""
                          }
                          type={
                            handleFormatStatus(
                              get(item, "status", "").toString()
                            )?.color ?? ("" as any)
                          }
                        />
                      </td>

                      <td className="td min-w-40">{createdAt}</td>

                      <td className="td min-w-40">{expiredAt}</td>

                      <td className="td py-4 pl-3 pr-4 sm:pr-6 lg:pr-8 flex items-center gap-2">
                        {isHaveActionPermission(
                          "",
                          "group_buy_management",
                          "view"
                        ) && (
                          <Button
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                            onClick={() => {
                              navigate(
                                `/group-management/detail/${get(
                                  item,
                                  "id",
                                  ""
                                )}`
                              );
                            }}
                          >
                            <EyeIcon className="w-4 h-4 text-black" />
                          </Button>
                        )}

                        {isHaveActionPermission(
                          "",
                          "group_buy_management",
                          "edit"
                        ) && (
                          <Button
                            className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300"
                            onClick={() => {
                              navigate(
                                `/group-management/edit/${get(
                                  item,
                                  "id",
                                  ""
                                )}?event_id=${get(item, "event_id", "")}`
                              );
                            }}
                          >
                            <PencilIcon className="w-4 h-4 text-black" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Pagination
        currentPage={get(paging, "current_page", 1)}
        totalPage={get(paging, "total_page", 0)}
        total={{
          totalItems: get(paging, "total_item", 0),
          total: get(paging, "total", 0),
        }}
        onChange={handleChangePage}
      />
    </section>
  );
};

export default DataTable;
